import React, { useState, useEffect } from 'react';
import { Zap, Check, Flame, ChevronRight, Star } from 'lucide-react';
import { apiClient } from '../../api/client';

const CATEGORY_COLORS = {
  social: 'border-blue-400 bg-blue-50 text-blue-700',
  kindness: 'border-pink-400 bg-pink-50 text-pink-700',
  listening: 'border-green-400 bg-green-50 text-green-700',
  confidence: 'border-amber-400 bg-amber-50 text-amber-700',
  empathy: 'border-indigo-400 bg-indigo-50 text-indigo-700',
  assertiveness: 'border-purple-400 bg-purple-50 text-purple-700',
  gratitude: 'border-teal-400 bg-teal-50 text-teal-700',
};

const DIFFICULTY_STYLE = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  hard: 'bg-red-100 text-red-700',
};

const DailySocialDareCard = () => {
  const [dare, setDare] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [reflection, setReflection] = useState('');
  const [showReflection, setShowReflection] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/daily-dare').then(setDare).catch(() => {});
  }, []);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const res = await apiClient.request('/api/self-dev/daily-dare/complete', {
        method: 'POST',
        body: JSON.stringify({ reflection }),
      });
      setResult(res);
      setDare(prev => prev ? { ...prev, completed: true } : prev);
    } catch {}
    setCompleting(false);
  };

  if (!dare) return null;

  const catStyle = CATEGORY_COLORS[dare.category] || CATEGORY_COLORS.social;
  const diffStyle = DIFFICULTY_STYLE[dare.difficulty] || DIFFICULTY_STYLE.easy;

  return (
    <div className={`rounded-2xl border-2 ${dare.completed ? 'border-green-300 bg-green-50/50' : catStyle.split(' ')[0] + ' bg-white'} shadow-sm overflow-hidden`} data-testid="daily-dare-card">
      {/* Header */}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${dare.completed ? 'bg-green-500' : 'bg-gradient-to-br from-orange-400 to-red-500'}`}>
              {dare.completed ? <Check size={16} className="text-white" /> : <Zap size={16} className="text-white" />}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Today&apos;s Social Dare</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${diffStyle}`}>{dare.difficulty}</span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${catStyle.split(' ').slice(1).join(' ')}`}>{dare.category}</span>
              </div>
            </div>
          </div>
          {dare.streak > 0 && (
            <div className="flex items-center gap-1 text-xs" data-testid="dare-streak">
              <Flame size={14} className="text-orange-500" />
              <span className="font-bold text-gray-700">{dare.streak}d</span>
            </div>
          )}
        </div>

        <h3 className="text-base sm:text-lg font-bold text-gray-900 mt-3" data-testid="dare-title">{dare.title}</h3>
        <p className="text-sm text-gray-600 mt-1">{dare.description}</p>

        {/* Reward */}
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Star size={12} className="text-amber-500" /> +{dare.hearts_reward} hearts</span>
          <span>+{dare.xp} XP</span>
          {dare.streak > 0 && <span className="text-orange-500 font-medium">{dare.streak}-day streak</span>}
        </div>

        {/* Completion */}
        {dare.completed ? (
          <div className="mt-3 p-3 bg-green-100 rounded-xl">
            <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
              <Check size={14} /> Dare completed today!
            </p>
            {result && result.streak_bonus > 0 && (
              <p className="text-xs text-green-600 mt-1">Streak bonus: +{result.streak_bonus} hearts!</p>
            )}
            {result && result.nft_earned && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dare-nft-reward">
                <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
                  <Star size={12} className="text-amber-500" /> NFT Earned: {result.nft_earned.name}
                </p>
                <p className="text-[10px] text-amber-600 mt-0.5">{result.nft_earned.description}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3">
            {showReflection ? (
              <div className="space-y-2">
                <textarea
                  value={reflection}
                  onChange={e => setReflection(e.target.value)}
                  placeholder="Quick reflection: How did it go? (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  rows={2}
                  data-testid="dare-reflection-input"
                />
                <button
                  onClick={handleComplete}
                  disabled={completing}
                  className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl text-sm hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="dare-complete-btn"
                >
                  {completing ? 'Completing...' : 'I Did It!'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowReflection(true)}
                className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl text-sm hover:shadow-lg transition-all flex items-center justify-center gap-1.5"
                data-testid="dare-accept-btn"
              >
                <Zap size={14} /> Accept the Dare
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Stats Footer */}
      {dare.total_completed > 0 && (
        <div className="px-4 sm:px-5 py-2.5 bg-gray-50 border-t border-gray-100 flex justify-between text-[10px] text-gray-400">
          <span>{dare.total_completed} dares completed</span>
          <span>Best streak: {dare.longest_streak}d</span>
        </div>
      )}
    </div>
  );
};

export default DailySocialDareCard;
